/**
 * RBAC per viaggio.
 *
 * Fonte unica di verita' dei permessi: usata sia dalle route API sia dalla UI (per
 * nascondere azioni non consentite). L'enforcement reale avviene comunque
 * server-side nel repository layer: questo modulo non e' una difesa, e' la regola.
 */

export const TRIP_ROLES = ['owner', 'editor', 'viewer'] as const;
export type TripRole = (typeof TRIP_ROLES)[number];

/** Rank crescente: un ruolo piu' alto include i permessi di quelli piu' bassi. */
const ROLE_RANK: Record<TripRole, number> = {
  viewer: 1,
  editor: 2,
  owner: 3,
};

export function isTripRole(value: unknown): value is TripRole {
  return typeof value === 'string' && (TRIP_ROLES as readonly string[]).includes(value);
}

export function hasAtLeastRole(role: TripRole, minimum: TripRole): boolean {
  return ROLE_RANK[role] >= ROLE_RANK[minimum];
}

export const TRIP_ACTIONS = [
  'trip:read',
  'trip:update',
  'trip:delete',
  'itinerary:read',
  'itinerary:write',
  'expense:read',
  'expense:write',
  'document:read',
  'document:write',
  'checklist:write',
  'member:read',
  'member:invite',
  'member:change_role',
  'member:remove',
  'vote:cast',
  'comment:write',
] as const;

export type TripAction = (typeof TRIP_ACTIONS)[number];

/**
 * Ruolo minimo richiesto per ogni azione.
 *
 * Nota: un Viewer puo' votare e commentare (brief §20) ma non modificare nulla.
 */
const MIN_ROLE_BY_ACTION: Record<TripAction, TripRole> = {
  'trip:read': 'viewer',
  'trip:update': 'editor',
  'trip:delete': 'owner',
  'itinerary:read': 'viewer',
  'itinerary:write': 'editor',
  'expense:read': 'viewer',
  'expense:write': 'editor',
  'document:read': 'viewer',
  'document:write': 'editor',
  'checklist:write': 'editor',
  'member:read': 'viewer',
  'member:invite': 'owner',
  'member:change_role': 'owner',
  'member:remove': 'owner',
  'vote:cast': 'viewer',
  'comment:write': 'viewer',
};

export function minimumRoleFor(action: TripAction): TripRole {
  return MIN_ROLE_BY_ACTION[action];
}

export function can(role: TripRole, action: TripAction): boolean {
  return hasAtLeastRole(role, MIN_ROLE_BY_ACTION[action]);
}

/**
 * L'owner non puo' declassare o rimuovere se stesso: un viaggio ha sempre
 * esattamente un owner (invariante garantita anche da trigger sul database).
 */
export function canChangeRoleOf(params: {
  actorRole: TripRole;
  actorUserId: string;
  targetUserId: string;
}): boolean {
  if (!can(params.actorRole, 'member:change_role')) return false;
  return params.actorUserId !== params.targetUserId;
}

export function canRemoveMember(params: {
  actorRole: TripRole;
  actorUserId: string;
  targetUserId: string;
}): boolean {
  // Chiunque puo' abbandonare un viaggio, tranne l'owner (deve prima trasferirlo).
  if (params.actorUserId === params.targetUserId) return params.actorRole !== 'owner';
  return can(params.actorRole, 'member:remove');
}
